<template>
  <div class="BirthDateForm">
    <h2 class="gl_form-title">
      {{ t(`copy_life_birthdate-form_title-${props.answers.forWhom}`) }}
    </h2>

    <FormField :statement="birthDateStatement">
      <BirthDatePickerComponent
        id="birthDate"
        v-model="birthDate"
        :rules="[
          validationRules.required,
          validationRules.notFutureDate,
          validationRules.validateActuarialAge
        ]"
      />
    </FormField>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRules } from '@/modules/core/composables/useRulesComposable'
import Insured from '@/modules/common/common/models/APP/Insured'
import BirthDatePickerComponent from '@/modules/core/components/formFields/BirthDatePickerComponent.vue'
import FormField from '@/modules/core/components/utils/FormField.vue'
import type LifeConfigurations from '@/modules/life/common/models/APP/LifeConfigurations'
import type LifeRaterAnswers from '@/modules/life/rater/models/APP/LifeRaterAnswers'
import { ForWhomEnum } from '@/modules/core/models/enums'

const { t } = useI18n()
const { required, validateActuarialAge, notFutureDate } = useRules()

defineExpose({ triggerSubmit })
interface PropsInterface {
  nextForm: String[]
  answers: LifeRaterAnswers
  configurations: LifeConfigurations
}
const props = withDefaults(defineProps<PropsInterface>(), {})

const { holderInsured, insured } = props.configurations.hiringMaxAges!
const isHolder = props.answers.forWhom === ForWhomEnum.ME
const minAge = isHolder ? Number(holderInsured.min) : Number(insured.min)
const maxAge = isHolder ? Number(holderInsured.max) : Number(insured.max)

const validationRules = {
  required,
  notFutureDate,
  validateActuarialAge: (value: string) => {
    const customMinText = `common.validations.min-age${props.answers.forWhom === ForWhomEnum.ME ? '' : '-insured'}`
    return validateActuarialAge({
      date: value,
      minAge,
      maxAge,
      customMinText
    })
  }
}

const birthDate = ref(props.answers.insureds[0].userDetails.birthDate)

const birthDateStatement = computed(() => {
  const i18nRoot = 'rater.common.more-info.birth-date.title'
  if (props.answers.forWhom === ForWhomEnum.ME) {
    return t(`${i18nRoot}-me`)
  }

  return t(`${i18nRoot}-not-me-singular`)
})

function triggerSubmit() {
  const page = props.nextForm[0]

  const insured = props.answers.insureds[0] ?? new Insured()
  insured.userDetails.birthDate = birthDate.value

  const answers = {
    insureds: [insured]
  }

  return { page, answers }
}
</script>
